import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import {
  themes,
  scrollTop,
  optionalAvathar,
  tableRowSx,
} from "services/constants";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useFetch } from "services/hooks/useFetch";
import { useSelector } from "react-redux";
import { Loader } from "components/Loader";
import { InfoMessage } from "components/CustomComponents/CustomButton";

const keys = [
  "status",
  "user_name",
  "amount",
  "repayment_date",
  "advance_type",
  "description",
  "reason",
];

export const History = ({
  departmentChange,
  setDepartmentChange,
  userChange,
  setUserChange,
  overTimeScreen,
  departmentId,
  userId,
  startDate,
  endDate,
}) => {
  const { domain, userDetails, globalSearchValue, topbarCompanyId } =
    useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRow, setExpandedRow] = useState(null);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [updateData, setUpdateData] = useState(true);
  const containerRef = useRef(null);

  const {
    result: advanceDetails,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}advances/?user_id=${userDetails.id}&approved_reject_status=Approved&department_id=${departmentId}&start_date=${startDate}&end_date=${endDate}&selected_user_id=${userId}&company_id=${topbarCompanyId}`
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
    }
  }, [reCallApi, topbarCompanyId]);

  useEffect(() => {
    if ((departmentChange || userChange) && overTimeScreen === "History") {
      reCallApi(true);
      setDepartmentChange(false);
      setUserChange(false);
    }
  }, [
    departmentChange,
    overTimeScreen,
    reCallApi,
    setDepartmentChange,
    setUserChange,
    userChange,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (rowId) => {
    if (expandedRow === rowId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(rowId);
    }
  };

  useEffect(() => {
    if (updateData && advanceDetails?.length !== 0 && advanceDetails) {
      setSalaryHistory(advanceDetails);
      setUpdateData(false);
    }
  }, [advanceDetails, updateData]);

  useEffect(() => {
    if (advanceDetails?.length !== 0 || advanceDetails?.length === 0) {
      const filteredResults = advanceDetails?.filter((item) =>
        keys.some((key) =>
          item[key]
            ?.toString()
            .toLowerCase()
            .includes(globalSearchValue.toString().toLowerCase())
        )
      );
      setSalaryHistory(filteredResults);
      setPage(0);
    }
  }, [globalSearchValue, advanceDetails]);

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  return loading ? (
    <Loader info="Loading..." />
  ) : (
    <React.Fragment>
      <Mui.Grid container>
        <Mui.TableContainer
          ref={containerRef}
          sx={{ width: "100%", maxHeight: 724, mt: 2, ml: 2, mr: 2 }}
        >
          <Mui.Table className="border">
            <Mui.TableHead
              sx={{
                backgroundColor: themes.primary,
                position: "sticky",
                top: -1,
                zIndex: 10,
              }}
            >
              <Mui.TableRow sx={tableRowSx}>
                <Mui.TableCell></Mui.TableCell>
                <Mui.TableCell>
                  <Mui.Typography fontWeight="bold">
                    Employee Name
                  </Mui.Typography>
                </Mui.TableCell>
                <Mui.TableCell>
                  <Mui.Typography fontWeight="bold">Date</Mui.Typography>
                </Mui.TableCell>
                <Mui.TableCell>
                  <Mui.Typography fontWeight="bold">Amount</Mui.Typography>
                </Mui.TableCell>
                <Mui.TableCell>
                  <Mui.Typography fontWeight="bold">
                    Advance Type
                  </Mui.Typography>
                </Mui.TableCell>
                <Mui.TableCell>
                  <Mui.Typography fontWeight="bold">Status</Mui.Typography>
                </Mui.TableCell>
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {salaryHistory.length === 0 ? (
                <InfoMessage dataLength={advanceDetails} />
              ) : (
                salaryHistory
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <>
                      <Mui.TableRow sx={tableRowSx}>
                        <Mui.TableCell>
                          {expandedRow === item.id ? (
                            <Mui.IconButton
                              onClick={() => {
                                setExpandedRow(null);
                              }}
                            >
                              <KeyboardArrowDownIcon
                                style={{
                                  color: "black",
                                  fontWeight: "bold",
                                }}
                              />
                            </Mui.IconButton>
                          ) : (
                            <Mui.IconButton
                              onClick={() => {
                                handleRowClick(item.id);
                              }}
                            >
                              <KeyboardArrowRightIcon
                                style={{
                                  color: "black",
                                  fontWeight: "bold",
                                }}
                              />
                            </Mui.IconButton>
                          )}
                        </Mui.TableCell>
                        <Mui.TableCell>
                          <Mui.Stack direction="Row">
                            <Mui.Avatar
                              sx={{ width: 23, height: 23 }}
                              alt={item?.user_name?.charAt(0).toUpperCase()}
                              src={item?.image_url ?? optionalAvathar}
                            />
                            &nbsp;
                            {item?.user_name}
                          </Mui.Stack>
                        </Mui.TableCell>
                        <Mui.TableCell>{item?.repayment_date}</Mui.TableCell>

                        <Mui.TableCell>{item?.amount}</Mui.TableCell>
                        <Mui.TableCell>{item?.advance_type}</Mui.TableCell>
                        <Mui.TableCell>{item?.status}</Mui.TableCell>
                      </Mui.TableRow>
                      {expandedRow === item.id && (
                        <Mui.TableRow sx={tableRowSx}>
                          <Mui.TableCell colSpan={9}>
                            <Mui.TableContainer
                              sx={{
                                marginTop: 0.5,
                                marginLeft: "auto",
                                marginRight: "auto",
                                width: "100%px",
                              }}
                              className="w-96 border rounded"
                            >
                              <Mui.Table className="border">
                                <Mui.Typography
                                  variant="body1"
                                  gutterBottom
                                  sx={{ marginLeft: 1, marginTop: 0.5 }}
                                >
                                  <strong>Description :</strong>{" "}
                                  <Mui.Typography
                                    fontSize={15}
                                    color="textSecondary"
                                    sx={{ marginLeft: 13 }}
                                  >
                                    {item.description}
                                  </Mui.Typography>
                                </Mui.Typography>
                              </Mui.Table>
                            </Mui.TableContainer>
                            {item.status === "Rejected" && (
                              <Mui.TableContainer
                                sx={{
                                  marginTop: 0.5,
                                  marginLeft: "auto",
                                  marginRight: "auto",
                                  width: "100%px",
                                }}
                                className="w-96 border rounded"
                              >
                                <Mui.Table className="border">
                                  <Mui.Typography
                                    variant="body1"
                                    gutterBottom
                                    sx={{ marginLeft: 1, marginTop: 0.5 }}
                                  >
                                    <strong>Reason for Rejection :</strong>{" "}
                                    <Mui.Typography
                                      fontSize={15}
                                      color="textSecondary"
                                      sx={{ marginLeft: 13 }}
                                    >
                                      {item.reason}
                                    </Mui.Typography>
                                  </Mui.Typography>
                                </Mui.Table>
                              </Mui.TableContainer>
                            )}
                          </Mui.TableCell>
                        </Mui.TableRow>
                      )}
                    </>
                  ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
      </Mui.Grid>
      {salaryHistory.length > 25 && (
        <Mui.TablePagination
          className="custom-pagination"
          rowsPerPageOptions={[25, 50, 75, 100]}
          component="div"
          count={salaryHistory.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </React.Fragment>
  );
};
